import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from '@/lib/cn'

/**
 * واریانت‌ها با `cva` تعریف می‌شوند، نه با زنجیره‌ی شرط دست‌ساز.
 *
 * دلیل: با شرط دست‌ساز، افزودن یک واریانت تازه به ویرایش چند شرط تودرتو نیاز
 * داشت و تعارض کلاس‌ها به ترتیب رشته‌ها وابسته می‌شد.
 *
 * زبان بصری: دکمه‌ی اصلی گرادیان لهجه با هاله دارد، بقیه سطحِ آرام‌اند. در هر
 * ناحیه فقط یک دکمه‌ی اصلی؛ دو گرادیان کنار هم، اولویت را از بین می‌برد.
 */
const styles = cva(
  [
    'press inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium transition-[background,box-shadow,color,border-color,transform]',
    'duration-150 select-none',
    'disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-linear-100 from-accent to-accent-two text-ink-inverse',
          'shadow-glow hover:brightness-110 hover:-translate-y-px',
          // هایلایت درونی، لبه‌ی بالای دکمه را برجسته می‌کند.
          'ring-1 ring-inset ring-white/15',
        ],
        secondary: [
          'border border-line-strong bg-surface-raised text-ink shadow-raised',
          'hover:border-accent-line hover:bg-accent-soft/50 hover:text-accent',
        ],
        ghost: 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
        danger: [
          'bg-oppose text-ink-inverse ring-1 ring-inset ring-white/15',
          'shadow-raised hover:brightness-110 hover:-translate-y-px',
        ],
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 rounded-sm px-3 text-xs',
        md: 'h-10 rounded-md px-4 text-sm',
        lg: 'h-12 rounded-md px-6 text-[0.9375rem]',
        icon: 'size-10 rounded-md',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

interface ButtonProps
  extends ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof styles> {
  /** نشانگر بارگذاری. دکمه در این حالت خودبه‌خود غیرفعال می‌شود. */
  loading?: boolean
  icon?: ReactNode
}

export const Button = ({
  className,
  variant,
  size,
  loading = false,
  icon,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) => (
  <button
    // `type` پیش‌فرض `button` است نه `submit`. پیش‌فرض HTML باعث می‌شود هر
    // دکمه‌ی داخل فرم، فرم را ارسال کند — یک اشکال رایج و بی‌صدا.
    type={type}
    className={cn(styles({ variant, size }), className)}
    disabled={disabled === true || loading}
    // خواننده‌ی صفحه باید بفهمد عمل در حال انجام است.
    aria-busy={loading}
    {...rest}
  >
    {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
    {children}
  </button>
)
